<template>
  <div>
    <v-layout wrap>
      <v-flex xs3>
        <h5 class="subheading">{{ 'app.age'|trans }}</h5>
        <span>{{ `${patient.age} ${getAge(patient.age)}` }}</span>
      </v-flex>
      <v-flex xs3>
        <h5 class="subheading">{{ 'app.sex'|trans }}</h5>
        <span>{{ 'app.' + patient.sex | trans }}</span>
      </v-flex>
      <v-flex xs3>
        <h5 class="subheading">{{ 'app.interview.height'|trans }}</h5>
        <span>{{ interview.height }} cm</span>
      </v-flex>
      <v-flex xs3>
        <h5 class="subheading">{{ 'app.interview.bmi'|trans }}</h5>
        <span v-if="bmi.value">
          {{ bmi.value }}
          <v-icon small>{{ bmiCategory }}</v-icon>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon small v-on="on">help</v-icon>
            </template>
            <span>
              <span v-for="category in bmi.categories">
                <strong v-if="category.from === 0">
                  < {{ category.to }}
                </strong>
                <strong v-else-if="category.to === Infinity">
                  ≥ {{ category.from }}
                </strong>
                <strong v-else>
                  {{ category.from }} - {{ category.to }}
                </strong>
                - {{ `app.interview.bmi_level.` + category.name | trans }}
              </span>
            </span>
          </v-tooltip>
        </span>
        <span v-else>{{ `app.no_data`| trans }}</span>
      </v-flex>
    </v-layout>
    <v-layout wrap>
      <v-flex xs12>
        <h5 class="subheading">
          {{ 'app.body_weight'| trans }}
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon small v-on="on">help</v-icon>
            </template>
            <span v-html="trans('app.interview.body_weight_description')"></span>
          </v-tooltip>
        </h5>
      </v-flex>
      <v-flex xs3>
        <h5 class="mt-3 subheading">{{ 'app.interview.initial_weight_short'|trans }}</h5>
        <span>{{ interview.initial_weight }} kg</span>
      </v-flex>
      <v-flex xs3>
        <h5 class="mt-3 subheading">{{ 'app.interview.current_weight_short'|trans }}</h5>
        <span>{{ patient.current_weight }} kg</span>
      </v-flex>
      <v-flex xs3>
        <h5 class="mt-3 subheading">{{ 'app.interview.target_weight_short'|trans }}</h5>
        <span>{{ interview.target_weight }} kg</span>
      </v-flex>
      <v-flex xs3>
        <h5 class="mt-3 subheading">{{ 'app.interview.fair_weight_short'|trans }}</h5>
        <span>{{ patient.fair_weight }} kg</span>
      </v-flex>
    </v-layout>
    <v-layout wrap>
      <v-flex xs12 pa-2>
        <v-layout wrap>
          <v-flex xs12>
            <h5 class="mt-4 subheading">{{ `app.interview.activity` | trans }}</h5>
            <span>{{ 'app.interview.activities.' + interview.activity|trans }}</span>
          </v-flex>
        </v-layout>
        <v-layout wrap mt-3>
          <v-flex xs4 offset-xs4>
            <span class="text-bold">{{ `app.harris-benedict` | trans }}</span>
          </v-flex>
          <v-flex xs4>
            <span class="text-bold">{{ `app.mifflin` |trans }}</span>
          </v-flex>
        </v-layout>
        <v-layout wrap>
          <v-flex xs4>
            <span>{{ `app.interview.ppm` | trans }}</span>
            <v-tooltip right>
              <template v-slot:activator="{ on }">
                <v-icon small v-on="on">help</v-icon>
              </template>
              <span>{{ `app.interview.ppm_description_current` | trans }}</span>
            </v-tooltip>
          </v-flex>
          <v-flex xs4>
            <span>{{ bmrByHarrisBenedict }} kcal</span>
          </v-flex>
          <v-flex xs4>
            <span>{{ bmrByMifflin }} kcal</span>
          </v-flex>
        </v-layout>
        <v-layout wrap>
          <v-flex xs4>
            <span>{{ `app.interview.cpm` | trans }}</span>
            <v-tooltip right>
              <template v-slot:activator="{ on }">
                <v-icon small v-on="on">help</v-icon>
              </template>
              <span>{{ `app.interview.cpm_description_current` | trans }}</span>
            </v-tooltip>
          </v-flex>
          <v-flex xs4>
            <span>{{ tmrByHarrisBenedict }} kcal</span>
          </v-flex>
          <v-flex xs4>
            <span>{{ tmrByMifflin }} kcal</span>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
    <v-layout>
      <v-flex xs6>
        <h5 class="mt-4 subheading">{{ 'app.interview.estimated_target_weight_date'|trans }}
          <v-tooltip
              top
          >
            <template v-slot:activator="{ on }">
              <v-icon small v-on="on">help</v-icon>
            </template>
            <span>{{ 'app.interview.estimated_target_weight_date_description'| trans }}</span>
          </v-tooltip>
        </h5>
        <span v-if="weightChangeTargetDate">{{ weightChangeTargetDate }}</span>
        <span v-else>{{ `app.no_data`| trans }}</span>
      </v-flex>
      <v-flex xs6>
        <h5 v-if="showPsychologicalCondition" class="mt-4 subheading">
          {{ `app.interview.psychological_condition` | trans }}</h5>
        <span v-if="showPsychologicalCondition">{{
            'app.interview.psychological_conditions.' + interview.psychological_condition|trans
          }}</span>
      </v-flex>
    </v-layout>
  </div>
</template>
<script>
import {helpers} from "../../../helpers";
import {mapGetters} from "vuex";
import {bmiFactory} from "../../../factory/bmi.factory";
import {polishPlurals} from "polish-plurals";
import {trans} from "../../../app";

export default {
  computed: {
    ...mapGetters(['patient', 'interview']),
    bmiCategory: function () {
      if (this.bmi.category) {
        switch (this.bmi.category.name) {
          case 'starvation':
          case 'emaciation':
          case 'underweight':
            return 'arrow_downward';
          case 'overweight':
          case 'obese':
          case 'obese_1':
          case 'obese_2':
          case 'obese_3':
            return 'arrow_upward';
          default:
            return 'check';
        }
      }
      return null;
    },
    bmi: function () {
      if (this.patient && this.patient.sex && this.patient.age && this.patient.current_weight && this.interview && this.interview.height) {
        return bmiFactory.create(this.patient.sex, this.patient.age, this.patient.current_weight, this.interview.height);
      } else {
        return {
          value: null,
          category: null,
        }
      }
    },
    showPsychologicalCondition: function () {
      return this.patient.sex === 'female' && this.patient.age > 9 && this.interview.psychological_condition;
    },
    weightChangeTargetDate: function () {
      return helpers.calculateTargetWeightDate(
          this.patient.current_weight,
          this.interview.target_weight,
          this.interview.weight_change_per_week
      );
    },
    bmrByHarrisBenedict: function () {
      return helpers.calculateBmrByHarrisBenedict(
          this.patient.sex,
          this.patient.current_weight,
          this.interview.height,
          this.patient.age
      );
    },
    bmrByMifflin: function () {
      return helpers.calculateBmrByMifflin(
          this.patient.sex,
          this.patient.current_weight,
          this.interview.height,
          this.patient.age
      )
    },
    tmrByHarrisBenedict: function () {
      return helpers.calculateTmrByHarrisBenedict(
          this.patient.sex,
          this.patient.current_weight,
          this.interview.height,
          this.patient.age,
          this.interview.activity ? this.interview.activity : null,
          this.interview.psychological_condition ? this.interview.psychological_condition : null,
      )
    },
    tmrByMifflin: function () {
      return helpers.calculateTmrByMifflin(
          this.patient.sex,
          this.patient.current_weight,
          this.interview.height,
          this.patient.age,
          this.interview.activity ? this.interview.activity : null,
          this.interview.psychological_condition ? this.interview.psychological_condition : null,
      );
    }
  },
  methods:{
    getAge(age) {
      return polishPlurals(trans('app.year'), trans('app.years'), trans('app.years_genitive'), age);
    }
  }
}
</script>